import { TOOL_SCHEMAS, TOOL_HANDLERS } from "./tools";
import {
  getCerebrasSystemPromptSuffix,
  getChatbotSystemPrompt,
} from "./systemPrompt";
import {
  DEFAULT_CEREBRAS_MODEL,
  type LlmProviderId,
  type ResolvedLlmConfig,
  resolveLlmConfig,
} from "./llmConfig";
import { tryParseToolCallsFromAssistantText } from "./parseToolCallsFromText";
import { trimHistoryForLlm } from "./chatHistoryTrim";
import {
  FORCED_TOLL_AREA_STATISTICS_CHOICE,
  shouldForceGetTollAreaStatistics,
} from "./forceTollAreaStatisticsTool";

export interface DeepSeekMessage {
  role: "system" | "user" | "assistant" | "tool";
  content: string | null;
  name?: string;
  tool_call_id?: string;
  tool_calls?: {
    id: string;
    type: "function";
    function: { name: string; arguments: string };
  }[];
}

export interface StreamingToolCall {
  id: string;
  name: string;
  arguments: string;
  status: "running" | "success" | "error";
  result?: string;
}

export interface StreamHandlers {
  onDelta: (text: string) => void;
  onClearDisplay?: () => void;
  onToolCallStart: (call: StreamingToolCall) => void;
  onToolCallEnd: (call: StreamingToolCall) => void;
  onDone: (finalText: string) => void;
  onError: (err: Error) => void;
  signal?: AbortSignal;
}

const MAX_TOOL_ROUNDS = 5;

export class LlmHttpError extends Error {
  readonly status: number;

  constructor(status: number, body: string) {
    super(formatLlmHttpError(status, body));
    this.name = "LlmHttpError";
    this.status = status;
  }
}

function formatLlmHttpError(status: number, body: string): string {
  const snippet =
    body.length > 280 ? `${body.slice(0, 280)}…` : body;
  if (status === 429) {
    return (
      "Nhà cung cấp LLM từ chối do quá nhiều yêu cầu (429). Đợi vài giây rồi thử lại." +
      (snippet ? ` ${snippet}` : "")
    );
  }
  return `LLM API ${status}${snippet ? `: ${snippet}` : ""}`;
}

/** Chờ trước khi gọi lại sau 429; hủy khi AbortSignal kích hoạt */
function sleepMs(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const id = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    function onAbort() {
      clearTimeout(id);
      reject(new DOMException("Aborted", "AbortError"));
    }
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

const CHAT_COMPLETION_MAX_RETRIES = 3;

/** Lỗi Cerebras cho phép thử lại với DeepSeek (hết quota, quá tải, v.v.) */
function shouldFallbackFromCerebras(err: unknown): boolean {
  if (err instanceof DOMException && err.name === "AbortError") {
    return false;
  }
  if (err instanceof LlmHttpError) {
    return [401, 402, 403, 429, 502, 503, 529].includes(err.status);
  }
  if (err instanceof TypeError) {
    return true;
  }
  return false;
}

export class DeepSeekClient {
  /** Cerebras lỗi / hết hạn — các lần gửi sau dùng DeepSeek cho session này */
  private cerebrasUnavailable = false;

  /** Cần ít nhất một trong hai: Cerebras hoặc DeepSeek */
  isChatConfigured(): boolean {
    const c = resolveLlmConfig("cerebras", {
      cerebrasModel: DEFAULT_CEREBRAS_MODEL,
    });
    const d = resolveLlmConfig("deepseek");
    return !!(c.apiKey || d.apiKey);
  }

  /**
   * Ưu tiên Cerebras + model Qwen cố định; nếu lỗi phù hợp thì chuyển sang DeepSeek (một lần mỗi tin nhắn).
   */
  async chat(
    history: { role: "user" | "assistant"; content: string }[],
    userInput: string,
    handlers: StreamHandlers
  ) {
    if (!this.isChatConfigured()) {
      handlers.onError(
        new Error(
          "Trợ lý chưa được bật cho nhà máy này. Quản trị viên có thể cấu hình trong Quản trị hệ thống → Cấu hình nhà máy và tích hợp → Trợ lý AI."
        )
      );
      return;
    }

    const tryCerebrasFirst =
      !this.cerebrasUnavailable &&
      !!resolveLlmConfig("cerebras", { cerebrasModel: DEFAULT_CEREBRAS_MODEL })
        .apiKey;
    const hasDeepseek = !!resolveLlmConfig("deepseek").apiKey;

    const run = async (provider: LlmProviderId) => {
      const cfg = resolveLlmConfig(
        provider,
        provider === "cerebras"
          ? { cerebrasModel: DEFAULT_CEREBRAS_MODEL }
          : undefined
      );
      if (!cfg.apiKey) {
        throw new Error(`Thiếu ${cfg.envKeyName}`);
      }

      const systemContent =
        getChatbotSystemPrompt() +
        (provider === "cerebras" ? getCerebrasSystemPromptSuffix() : "");

      const trimmedHistory = trimHistoryForLlm(history, provider, cfg.model);

      const messages: DeepSeekMessage[] = [
        { role: "system", content: systemContent },
        ...trimmedHistory.map((m) => ({ role: m.role, content: m.content })),
        { role: "user", content: userInput },
      ];

      const state = { accumulated: "" };
      const deltaHandler = (delta: string) => {
        state.accumulated += delta;
        handlers.onDelta(delta);
      };

      const forceTollAreaStats =
        shouldForceGetTollAreaStatistics(userInput);

      for (let round = 0; round < MAX_TOOL_ROUNDS; round++) {
        const toolChoice =
          round === 0 && forceTollAreaStats
            ? FORCED_TOLL_AREA_STATISTICS_CHOICE
            : "auto";

        let { text, toolCalls } = await this.streamOnce(
          cfg,
          provider,
          messages,
          deltaHandler,
          handlers.signal,
          toolChoice
        );

        if ((!toolCalls || toolCalls.length === 0) && text?.trim()) {
          const parsed = tryParseToolCallsFromAssistantText(text.trim());
          if (parsed?.length) {
            toolCalls = parsed;
            state.accumulated = "";
            handlers.onClearDisplay?.();
            text = "";
          }
        }

        if (!toolCalls || toolCalls.length === 0) {
          handlers.onDone(state.accumulated);
          return;
        }

        messages.push({
          role: "assistant",
          content: text || null,
          tool_calls: toolCalls.map((t) => ({
            id: t.id,
            type: "function",
            function: { name: t.name, arguments: t.arguments },
          })),
        });

        for (const call of toolCalls) {
          const record: StreamingToolCall = {
            id: call.id,
            name: call.name,
            arguments: call.arguments,
            status: "running",
          };
          handlers.onToolCallStart(record);

          const handler = TOOL_HANDLERS[call.name];
          let resultStr = "";
          if (!handler) {
            resultStr = JSON.stringify({ error: `Tool ${call.name} không tồn tại` });
            record.status = "error";
          } else {
            try {
              const parsed = call.arguments
                ? JSON.parse(call.arguments)
                : {};
              const result = await handler(parsed);
              resultStr = JSON.stringify(result);
              record.status = "success";
            } catch (err) {
              resultStr = JSON.stringify({
                error: err instanceof Error ? err.message : String(err),
              });
              record.status = "error";
            }
          }
          record.result = resultStr;
          handlers.onToolCallEnd(record);

          messages.push({
            role: "tool",
            tool_call_id: call.id,
            content: resultStr,
          });
        }
      }

      handlers.onDone(state.accumulated);
    };

    try {
      if (tryCerebrasFirst) {
        try {
          await run("cerebras");
          return;
        } catch (err) {
          if (handlers.signal?.aborted) {
            return;
          }
          if (shouldFallbackFromCerebras(err) && hasDeepseek) {
            this.cerebrasUnavailable = true;
            handlers.onClearDisplay?.();
            await run("deepseek");
            return;
          }
          throw err;
        }
      }

      if (hasDeepseek) {
        await run("deepseek");
        return;
      }

      handlers.onError(
        new Error(
          "Không kết nối được Cerebras. Quản trị viên có thể cấu hình DeepSeek để dự phòng trong phần Trợ lý AI."
        )
      );
    } catch (err) {
      if (handlers.signal?.aborted) {
        return;
      }
      handlers.onError(err instanceof Error ? err : new Error(String(err)));
    }
  }

  private async streamOnce(
    cfg: ResolvedLlmConfig,
    provider: LlmProviderId,
    messages: DeepSeekMessage[],
    onDelta: (text: string) => void,
    signal: AbortSignal | undefined,
    toolChoice:
      | "auto"
      | { type: "function"; function: { name: string } } = "auto"
  ): Promise<{
    text: string;
    toolCalls: { id: string; name: string; arguments: string }[];
  }> {
    const body: Record<string, unknown> = {
      model: cfg.model,
      stream: true,
      messages,
      tools: TOOL_SCHEMAS,
      tool_choice: toolChoice,
    };
    if (provider === "cerebras") {
      body.temperature = 0.25;
      body.parallel_tool_calls = false;
    }

    const url = `${cfg.baseUrl}/chat/completions?v=1.0`;
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${cfg.apiKey}`,
    };
    const payload = JSON.stringify(body);

    let nextDelayMs = 0;
    for (let attempt = 0; attempt <= CHAT_COMPLETION_MAX_RETRIES; attempt++) {
      if (attempt > 0) {
        await sleepMs(nextDelayMs, signal);
      }

      const res = await fetch(url, {
        method: "POST",
        headers,
        body: payload,
        signal,
      });

      if (res.ok && res.body) {
        return this.readChatCompletionStream(res, onDelta);
      }

      const errText = await res.text().catch(() => "");
      if (res.status === 429 && attempt < CHAT_COMPLETION_MAX_RETRIES) {
        const ra = res.headers.get("Retry-After");
        const sec = ra ? parseFloat(ra) : NaN;
        nextDelayMs = Number.isFinite(sec) && sec > 0
          ? Math.min(sec * 1000, 60_000)
          : Math.min(1500 * Math.pow(2, attempt), 30_000);
        continue;
      }

      throw new LlmHttpError(res.status, errText);
    }

    throw new Error("LLM: không nhận được phản hồi hợp lệ sau khi thử lại.");
  }

  private async readChatCompletionStream(
    res: Response,
    onDelta: (text: string) => void
  ): Promise<{
    text: string;
    toolCalls: { id: string; name: string; arguments: string }[];
  }> {
    const reader = res.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let fullText = "";
    const toolCallsAcc: Record<
      number,
      { id: string; name: string; arguments: string }
    > = {};

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let idx: number;
      while ((idx = buffer.indexOf("\n")) !== -1) {
        const line = buffer.slice(0, idx).trim();
        buffer = buffer.slice(idx + 1);
        if (!line.startsWith("data:")) continue;
        const payload = line.slice(5).trim();
        if (payload === "[DONE]") continue;
        try {
          const event = JSON.parse(payload);
          const delta = event.choices?.[0]?.delta;
          if (!delta) continue;
          if (typeof delta.content === "string" && delta.content) {
            fullText += delta.content;
            onDelta(delta.content);
          }
          if (Array.isArray(delta.tool_calls)) {
            for (const tc of delta.tool_calls) {
              const index = tc.index ?? 0;
              if (!toolCallsAcc[index]) {
                toolCallsAcc[index] = {
                  id: tc.id ?? `call_${index}`,
                  name: tc.function?.name ?? "",
                  arguments: "",
                };
              }
              if (tc.id) toolCallsAcc[index].id = tc.id;
              if (tc.function?.name)
                toolCallsAcc[index].name = tc.function.name;
              if (tc.function?.arguments)
                toolCallsAcc[index].arguments += tc.function.arguments;
            }
          }
        } catch {
          // ignore malformed chunk
        }
      }
    }

    return {
      text: fullText,
      toolCalls: Object.values(toolCallsAcc).filter((t) => t.name),
    };
  }
}

export const deepseekClient = new DeepSeekClient();
